// Admin → Dashboard. The main view (owner ruling 2026-08-25).
//
// Layout, top to bottom, in the order an operator actually needs it:
//   1. Attention — what needs doing today. First, because everything else is
//      information and this is action.
//   2. Headline tiles — money, people, content.
//   3. Charts — revenue per currency, signups.
//   4. Breakdowns — plans, statuses, locale, geo.
//   5. Recent activity — invoices and signups, clickable through.
//
// 🔴 Two honesty rules this page keeps:
//   · Money is NEVER summed across currencies (no FX table exists).
//   · The geo panel states its own COVERAGE, because country is optional on a
//     profile — implying "everyone is in Qatar" from 3 of 8 rows would be worse
//     than showing nothing.

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import {
  BarChart,
  DonutChart,
  ErrorState,
  Icon,
  LineChart,
  Money,
  PageHeader,
  StatGrid,
  StatTile,
  StatusBadge,
  type IconName,
} from '~/components';
import { TONE_TILE, type IconTone } from '~/components/iconTone';
import { countryFlag, countryName, getDashboardOverview } from '~/lib/dashboard';
import { formatMinorAmount } from '~/lib/money';

export const Route = createFileRoute('/_authed/dashboard')({
  component: DashboardPage,
});

const WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

function DashboardPage() {
  const [days, setDays] = useState(30);

  const overview = useQuery({
    queryKey: ['dashboard', 'overview', days],
    queryFn: ({ signal }) => getDashboardOverview(days, signal),
  });

  const d = overview.data;
  const loading = overview.isLoading;

  // Growth vs the immediately preceding window of the same length.
  const growth =
    d && d.people.newInPrevWindow > 0
      ? (d.people.newInWindow - d.people.newInPrevWindow) / d.people.newInPrevWindow
      : null;

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Dashboard"
        description="Platform-wide health at a glance."
        actions={
          <div className="flex gap-1 rounded-lg border border-admin-200 bg-white p-0.5">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                type="button"
                onClick={() => setDays(w.days)}
                className={
                  days === w.days
                    ? 'rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white'
                    : 'rounded-md px-3 py-1.5 text-sm font-medium text-admin-600 hover:bg-admin-50'
                }
              >
                {w.label}
              </button>
            ))}
          </div>
        }
      />

      {overview.error && (
        <ErrorState
          error={overview.error}
          onRetry={() => void overview.refetch()}
          className="rounded-xl border border-admin-200 bg-white"
        />
      )}

      {!overview.error && (
        <>
          {/* ── 1. Attention ─────────────────────────────────────────────── */}
          <AttentionPanel attention={d?.attention} loading={loading} />

          {/* ── 2. Headline tiles ────────────────────────────────────────── */}
          <StatGrid className="mb-6">
            <StatTile
              label={`Revenue · last ${days}d`}
              icon="money"
              loading={loading}
              value={<CurrencyStack byCurrency={d?.money.revenueByCurrency} />}
              hint="never blended across currencies"
            />
            <StatTile
              label="Paying subscriptions"
              icon="card"
              loading={loading}
              value={(d?.money.payingSubs ?? 0).toLocaleString('en-US')}
              hint={
                d
                  ? `${d.money.subsByStatus.past_due ?? 0} past due · free tier excluded`
                  : undefined
              }
            />
            <StatTile
              label={`New accounts · last ${days}d`}
              icon="userPlus"
              loading={loading}
              value={(d?.people.newInWindow ?? 0).toLocaleString('en-US')}
              delta={growth}
              // More signups is good; this is the one tile where "up" is
              // unambiguously the right direction.
              deltaGood="up"
              deltaLabel={`vs previous ${days}d`}
            />
            <StatTile
              label="Unsubscribed"
              icon="userMinus"
              loading={loading}
              value={(d?.people.unsubscribed ?? 0).toLocaleString('en-US')}
              hint={
                d && d.people.unsubscribed === 0
                  ? 'no suppressions yet'
                  : 'bounced, complained or opted out'
              }
            />
          </StatGrid>

          {/* ── 3. Charts ────────────────────────────────────────────────── */}
          <div className="mb-6 grid gap-4 lg:grid-cols-2">
            <LineChart
              title={`New accounts per day · last ${days}d`}
              height={200}
              data={(d?.people.signupSeries ?? []).map((p) => ({
                label: p.day.slice(5),
                value: p.value,
              }))}
              valueFormatter={(v) => String(Math.round(v))}
              color="var(--color-emerald-500)"
            />
            {/* One chart per currency — overlaying them on shared axes would
                imply the values are comparable, which they are not. */}
            {(d?.money.revenueSeries ?? []).slice(0, 3).map((s) => (
              <LineChart
                key={s.currency}
                title={`${s.currency} collected per day`}
                height={200}
                data={s.points.map((p) => ({ label: p.day.slice(5), value: p.value }))}
                valueFormatter={(v) => formatMinorAmount(v, s.currency)}
                color="var(--color-amber-500)"
              />
            ))}
          </div>

          {/* ── 4. Breakdowns ────────────────────────────────────────────── */}
          <div className="mb-6 grid gap-4 lg:grid-cols-3">
            <DonutChart
              title="Active subscriptions by plan"
              data={Object.entries(d?.money.subsByPlan ?? {}).map(([label, value]) => ({
                label,
                value,
              }))}
            />
            <BarChart
              title="Accounts by status"
              data={Object.entries(d?.people.byStatus ?? {}).map(([label, value]) => ({
                label,
                value,
              }))}
              color="var(--color-emerald-500)"
            />
            <GeoPanel geo={d?.geo} loading={loading} />
          </div>

          {/* ── 5. Content + learning health ─────────────────────────────── */}
          <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <MiniStat
              label="Pages live"
              value={d?.content.pagesLive}
              icon="page"
              tone="violet"
              to="/pages"
              loading={loading}
            />
            <MiniStat
              label="Posts published"
              value={d?.content.postsPublished}
              icon="edit"
              tone="violet"
              to="/blog"
              loading={loading}
            />
            <MiniStat
              label="Post drafts"
              value={d?.content.postsDraft}
              icon="edit"
              tone="amber"
              to="/blog"
              loading={loading}
            />
            <MiniStat
              label="Subjects"
              value={d?.content.subjects}
              icon="book"
              tone="sky"
              to="/content"
              loading={loading}
            />
            <MiniStat
              label="Lessons"
              value={d?.content.lessons}
              icon="sparkle"
              tone="sky"
              to="/content"
              loading={loading}
            />
            <MiniStat
              label="Media"
              value={d?.content.media}
              icon="image"
              tone="slate"
              to="/media"
              loading={loading}
            />
          </div>

          {/* ── 6. Recent activity ───────────────────────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-2">
            <RecentInvoices rows={d?.recentInvoices ?? []} loading={loading} />
            <RecentSignups rows={d?.recentSignups ?? []} loading={loading} />
          </div>
        </>
      )}
    </div>
  );
}

function AttentionPanel({
  attention,
  loading,
}: {
  attention?: { pastDue: number; openInvoices: number; cardsExpiring: number; draftPosts: number };
  loading: boolean;
}) {
  if (loading) return <div className="mb-6 h-20 animate-pulse rounded-xl bg-admin-100" />;
  if (!attention) return null;

  const items = [
    {
      key: 'pastDue',
      count: attention.pastDue,
      label: 'past-due subscriptions',
      to: '/subscriptions?status=past_due',
      icon: 'alert' as IconName,
      tone: 'rose' as IconTone,
    },
    {
      key: 'openInvoices',
      count: attention.openInvoices,
      label: 'unpaid invoices',
      to: '/payments?status=open',
      icon: 'receipt' as IconName,
      tone: 'amber' as IconTone,
    },
    {
      key: 'cardsExpiring',
      count: attention.cardsExpiring,
      label: 'cards expiring soon',
      to: '/subscriptions',
      icon: 'card' as IconName,
      tone: 'amber' as IconTone,
    },
    {
      key: 'draftPosts',
      count: attention.draftPosts,
      label: 'unpublished drafts',
      to: '/blog?published=false',
      icon: 'edit' as IconName,
      tone: 'violet' as IconTone,
    },
  ].filter((i) => i.count > 0);

  // An empty attention list is GOOD NEWS and should say so — rendering nothing
  // would read as "this panel is broken".
  if (items.length === 0) {
    return (
      <div className="mb-6 flex items-center gap-3 rounded-xl border border-success-500/20 bg-success-50 px-4 py-3">
        <Icon name="checkCircle" className="h-5 w-5 text-success-700" />
        <p className="text-sm font-medium text-success-700">
          Nothing needs attention — no past-due subscriptions, unpaid invoices or expiring cards.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl border border-admin-200 bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-admin-800">
        <Icon name="bell" className="h-4 w-4 text-rose-500" />
        Needs attention
      </h2>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((i) => (
          <Link
            key={i.key}
            to={i.to}
            className="flex items-center gap-3 rounded-lg border border-admin-200 p-3 transition-colors hover:bg-admin-50"
          >
            <span
              className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${TONE_TILE[i.tone]}`}
            >
              <Icon name={i.icon} className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-lg font-bold leading-tight text-admin-900">
                {i.count}
              </span>
              <span className="block truncate text-xs text-admin-500">{i.label}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function GeoPanel({
  geo,
  loading,
}: {
  geo?: {
    profilesTotal: number;
    withCountry: number;
    topCountries: Array<{ country: string; count: number }>;
  };
  loading: boolean;
}) {
  if (loading)
    return <div className="h-64 animate-pulse rounded-xl border border-admin-200 bg-white" />;

  const coverage =
    geo && geo.profilesTotal > 0 ? Math.round((geo.withCountry / geo.profilesTotal) * 100) : 0;

  return (
    <figure className="rounded-xl border border-admin-200 bg-white p-4">
      <figcaption className="mb-3 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-admin-800">
          <Icon name="globeAlt" className="h-4 w-4 text-sky-500" />
          Where accounts are
        </span>
      </figcaption>

      {!geo || geo.topCountries.length === 0 ? (
        <p className="py-10 text-center text-sm text-admin-400">
          No country recorded on any profile yet.
        </p>
      ) : (
        <>
          <ul className="space-y-2">
            {geo.topCountries.map((c) => {
              const pct = geo.withCountry === 0 ? 0 : (c.count / geo.withCountry) * 100;
              return (
                <li key={c.country} className="flex items-center gap-2.5 text-sm">
                  <span aria-hidden className="w-5 text-base leading-none">
                    {countryFlag(c.country)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-admin-700">
                    {countryName(c.country)}
                  </span>
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-admin-100">
                    <div className="h-full rounded-full bg-sky-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-6 text-end tabular-nums text-admin-800">{c.count}</span>
                </li>
              );
            })}
          </ul>

          {/* 🔴 State the coverage. Country is optional on a profile, so the
              ranking above describes only the minority who filled it in. */}
          <p className="mt-3 border-t border-admin-100 pt-2 text-xs text-admin-400">
            Based on {geo.withCountry} of {geo.profilesTotal} profiles ({coverage}%) — country is
            optional, so this is a partial picture.
          </p>
        </>
      )}
    </figure>
  );
}

function MiniStat({
  label,
  value,
  icon,
  tone,
  to,
  loading,
}: {
  label: string;
  value?: number;
  icon: IconName;
  tone: IconTone;
  to: string;
  loading: boolean;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 rounded-xl border border-admin-200 bg-white p-3 transition-colors hover:bg-admin-50"
    >
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${TONE_TILE[tone]}`}>
        <Icon name={icon} className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        {loading ? (
          <span className="mb-1 block h-4 w-8 animate-pulse rounded bg-admin-100" />
        ) : (
          <span className="block text-lg font-bold leading-tight text-admin-900">
            {(value ?? 0).toLocaleString('en-US')}
          </span>
        )}
        <span className="block truncate text-xs text-admin-500">{label}</span>
      </span>
    </Link>
  );
}

function CurrencyStack({ byCurrency }: { byCurrency?: Record<string, number> }) {
  const rows = Object.entries(byCurrency ?? {});
  if (rows.length === 0) return <span className="text-admin-400">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {rows.map(([currency, amountMinor]) => (
        <Money key={currency} amountMinor={amountMinor} currency={currency} emphasis="strong" />
      ))}
    </div>
  );
}

function RecentInvoices({
  rows,
  loading,
}: {
  rows: Array<{
    id: string;
    amountMinor: number;
    currency: string;
    status: string;
    issuedAt: string;
    paidAt: string | null;
  }>;
  loading: boolean;
}) {
  return (
    <PanelTable
      title="Recent invoices"
      icon="receipt"
      tone="amber"
      to="/payments"
      loading={loading}
    >
      {rows.length === 0 ? (
        <EmptyRow>No invoices yet.</EmptyRow>
      ) : (
        rows.map((r) => (
          <Link
            key={r.id}
            to="/payments"
            className="flex items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-admin-50"
          >
            <span className="flex min-w-0 items-center gap-2">
              <StatusBadge status={r.status} dot />
              <span className="truncate text-xs text-admin-500">{formatDate(r.issuedAt)}</span>
            </span>
            <Money amountMinor={r.amountMinor} currency={r.currency} />
          </Link>
        ))
      )}
    </PanelTable>
  );
}

function RecentSignups({
  rows,
  loading,
}: {
  rows: Array<{
    id: string;
    email: string | null;
    locale: string;
    status: string;
    createdAt: string;
  }>;
  loading: boolean;
}) {
  return (
    <PanelTable title="Recent signups" icon="userPlus" tone="emerald" to="/users" loading={loading}>
      {rows.length === 0 ? (
        <EmptyRow>No signups yet.</EmptyRow>
      ) : (
        rows.map((r) => (
          <Link
            key={r.id}
            to="/users/$id"
            params={{ id: r.id }}
            className="flex items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-admin-50"
          >
            <span className="min-w-0">
              <span className="block truncate text-sm text-admin-800">{r.email ?? '—'}</span>
              <span className="block text-xs text-admin-400">{formatDate(r.createdAt)}</span>
            </span>
            <StatusBadge status={r.locale} label={r.locale.toUpperCase()} tone="neutral" />
          </Link>
        ))
      )}
    </PanelTable>
  );
}

function PanelTable({
  title,
  icon,
  tone,
  to,
  loading,
  children,
}: {
  title: string;
  icon: IconName;
  tone: IconTone;
  to: string;
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-admin-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-admin-200 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-admin-800">
          <span className={`grid h-6 w-6 place-items-center rounded-md ${TONE_TILE[tone]}`}>
            <Icon name={icon} className="h-3.5 w-3.5" />
          </span>
          {title}
        </h2>
        <Link to={to} className="text-xs font-medium text-brand-600 hover:underline">
          View all
        </Link>
      </div>
      <div className="divide-y divide-admin-100">
        {loading
          ? Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="px-4 py-3">
                <div className="h-4 animate-pulse rounded bg-admin-100" />
              </div>
            ))
          : children}
      </div>
    </section>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-8 text-center text-sm text-admin-400">{children}</p>;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
